/**
 * WebUSB CH340 transport — implements [ch340.md](../docs/ch340.md) §3–§6. Web Serial is
 * not an option on Android (it enumerates Bluetooth RFCOMM ports only, §1), so the vendor
 * protocol is ours. Hardware-verified end to end on the phone (2026-07-23).
 *
 * `encodeBaud` is separated out so the §5 divisor formula is a unit test, not a field
 * surprise — its value for 115200 (`0xCC83`) was confirmed against real silicon.
 */
import { type Result, ok, err } from './result';
import type { Timers } from './timers';

/**
 * How long one bulk write may take before the page stops waiting on it (REQ-USB-9).
 *
 * A 40-byte write to a CH340 at 115200 completes in milliseconds, so this is not a performance
 * budget — it is the point past which the transfer is presumed wedged. It has to sit well inside
 * `sweep.py`'s 30 s `apply_far` timeout, because the whole purpose is that the laptop hears a
 * `fail` instead of nothing: worst case here is timeout + clearHalt + one retry, still ~10 s.
 *
 * A hidden tab freezes timers, so this cannot expire spuriously while the phone is in a pocket —
 * the timer and the transfer are both suspended together.
 */
export const WRITE_TIMEOUT_MS = 5_000;

/**
 * Consecutive halted reads tolerated before the stream gives up (REQ-USB-10).
 *
 * A halted IN endpoint returns immediately and forever, so clearing it and continuing without a
 * bound is a hot loop that pins the CPU and drains the battery with the screen off. Three is
 * enough to ride out a transient without becoming a spin.
 */
const MAX_CONSECUTIVE_HALTS = 3;

const REAL_TIMERS: Timers = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms) as unknown as number,
  clearTimeout: (h) => globalThis.clearTimeout(h),
  now: () => Date.now(),
};

// docs/ch340.md §3 — vendor control requests.
const REQ_READ_VERSION = 0x5f;
const REQ_WRITE_REG = 0x9a;
const REQ_SERIAL_INIT = 0xa1;
const REQ_MODEM_CTRL = 0xa4;

// docs/ch340.md §3 — register pairs (low | high << 8).
const REG_PRESCALER = 0x12;
const REG_DIVISOR = 0x13;
const REG_LCR = 0x18;
const REG_LCR2 = 0x25;
const REG_FLOW_CTL = 0x27;

const LCR_8N1 = 0xc3; // enable RX|TX (0x80|0x40) + 8 data bits (0x03), §4
const MODEM_DTR_RTS = 0x60; // RTS 0x40 | DTR 0x20, §6 step 6

/**
 * docs/ch340.md §5 — encode a baud rate into the 16-bit prescaler/divisor value.
 * `baud = 48e6 / (2^(12 - 3·ps - fact) · div)`; bit 7 is the buffering flag, set for
 * chip versions above 0x27.
 */
export function encodeBaud(baud: number, chipVersion: number): number {
  const CLK = 48_000_000;
  const clkDiv = (ps: number, fact: number): number => 1 << (12 - 3 * ps - fact);
  const minRate = (ps: number): number => Math.floor(CLK / (clkDiv(ps, 1) * 512));

  let ps = 3;
  for (; ps >= 0; ps--) if (baud > minRate(ps)) break;
  if (ps < 0) throw new RangeError(`baud ${baud} below supported range`);

  let fact = 1;
  let cd = clkDiv(ps, fact);
  let div = Math.floor(CLK / (cd * baud));

  if (div < 9 || div > 255) {
    div = Math.floor(div / 2);
    cd *= 2;
    fact = 0;
  }
  if (div < 2) throw new RangeError(`baud ${baud} above supported range`);

  // Pick the next divisor when it lands closer to the target; scaled to avoid rounding.
  if (
    Math.floor((16 * CLK) / (cd * div)) - 16 * baud >=
    16 * baud - Math.floor((16 * CLK) / (cd * (div + 1)))
  ) {
    div++;
  }

  // Prefer the lower base clock on an even divisor — a more error-tolerant receiver.
  if (fact === 1 && div % 2 === 0) {
    div /= 2;
    fact = 0;
  }

  let val = ((0x100 - div) << 8) | (fact << 2) | ps;
  if (chipVersion > 0x27) val |= 0x80; // §5 — without it the chip clumps output
  return val >>> 0;
}

export type Ch340Error =
  | { kind: 'claim'; cause: unknown }
  | { kind: 'init'; step: string; cause: unknown }
  | { kind: 'lost' };

/**
 * Why a write did not land (REQ-USB-9).
 *
 * `stalled` — the endpoint halted, or the transfer did not complete in `WRITE_TIMEOUT_MS`, and
 * recovery did not get the bytes through. The link may or may not still be usable; the caller
 * must not assume the board saw anything.
 * `lost` — the transfer rejected outright, which is what a departed device looks like.
 */
export type Ch340WriteError = 'stalled' | 'lost';

export type Ch340Port = {
  /** Chip version byte from READ_VERSION (§3), recorded in the log meta record. */
  version: number;
  /**
   * Send bytes to the board.
   *
   * Returns a value rather than throwing, and **never** resolves successfully on a transfer the
   * device rejected: WebUSB reports a halted endpoint as a `status` on a *fulfilled* promise, so
   * `await transferOut(...)` alone reports a wedged endpoint as a successful write
   * ([CODE_STYLE.md](../docs/CODE_STYLE.md) — expected failures are values).
   */
  write(bytes: Uint8Array): Promise<Result<void, Ch340WriteError>>;
  /** Stream received bytes until the device is lost or `close()` is called. */
  read(): AsyncIterableIterator<Uint8Array>;
  close(): Promise<void>;
};

/** Injected seams. Both default to the real thing; tests supply their own (REQ-NFR-4). */
export type Ch340Options = {
  timers?: Timers;
  writeTimeoutMs?: number;
};

/**
 * Claim the CH340 interface and bring it to `baud` 8N1 with flow control off (§6). The
 * device must already be chosen (via `requestDevice`); this does the init sequence and
 * returns a port, or a typed error naming the step that failed.
 */
export async function openCh340(
  device: USBDevice,
  baud: number,
  opts: Ch340Options = {},
): Promise<Result<Ch340Port, Ch340Error>> {
  const timers = opts.timers ?? REAL_TIMERS;
  const writeTimeoutMs = opts.writeTimeoutMs ?? WRITE_TIMEOUT_MS;
  try {
    await device.open();
    if (device.configuration == null) await device.selectConfiguration(1);
  } catch (cause) {
    return err({ kind: 'init', step: 'open', cause });
  }

  const iface = device.configuration?.interfaces.find(
    (i) => i.alternate.interfaceClass === 0xff,
  );
  if (iface === undefined) {
    return err({ kind: 'init', step: 'interface', cause: new Error('no vendor-class interface') });
  }
  const eps = iface.alternate.endpoints;
  const epIn = eps.find((e) => e.direction === 'in' && e.type === 'bulk');
  const epOut = eps.find((e) => e.direction === 'out' && e.type === 'bulk');
  if (epIn === undefined || epOut === undefined) {
    return err({ kind: 'init', step: 'endpoints', cause: new Error('no bulk endpoint pair') });
  }

  try {
    await device.claimInterface(iface.interfaceNumber);
  } catch (cause) {
    return err({ kind: 'claim', cause });
  }

  const co = (request: number, value: number, index: number): Promise<USBOutTransferResult> =>
    device.controlTransferOut({ requestType: 'vendor', recipient: 'device', request, value, index });

  let version: number;
  try {
    const v = await device.controlTransferIn(
      { requestType: 'vendor', recipient: 'device', request: REQ_READ_VERSION, value: 0, index: 0 },
      2,
    );
    if (v.status !== 'ok' || v.data == null || v.data.byteLength < 1) {
      return err({ kind: 'init', step: 'version', cause: new Error(`status ${v.status}`) });
    }
    version = v.data.getUint8(0);
  } catch (cause) {
    return err({ kind: 'init', step: 'version', cause });
  }

  try {
    await co(REQ_SERIAL_INIT, 0, 0);
    await co(REQ_WRITE_REG, (REG_DIVISOR << 8) | REG_PRESCALER, encodeBaud(baud, version));
    if (version >= 0x30) await co(REQ_WRITE_REG, (REG_LCR2 << 8) | REG_LCR, LCR_8N1);
    await co(REQ_MODEM_CTRL, ~MODEM_DTR_RTS & 0xffff, 0);
    await co(REQ_WRITE_REG, (REG_FLOW_CTL << 8) | REG_FLOW_CTL, 0x0000);
  } catch (cause) {
    return err({ kind: 'init', step: 'configure', cause });
  }

  const inEp = epIn.endpointNumber;
  const outEp = epOut.endpointNumber;
  let closed = false;

  /** Clear a halted endpoint. Returns whether the link is usable again (REQ-USB-10). */
  const clearHalt = async (dir: 'in' | 'out', ep: number): Promise<boolean> => {
    try {
      await device.clearHalt(dir, ep);
      return true;
    } catch {
      return false;
    }
  };

  type Attempt = 'ok' | 'halted' | 'timeout' | 'lost';

  /**
   * One bounded write. Distinguishes the three ways it can fail to arrive, because the caller's
   * next move differs for each — `halted` is a definite non-delivery, `timeout` is not.
   */
  const attempt = async (bytes: Uint8Array): Promise<Attempt> => {
    let handle: number | undefined;
    const expiry = new Promise<Attempt>((resolve) => {
      handle = timers.setTimeout(() => resolve('timeout'), writeTimeoutMs);
    });
    try {
      return await Promise.race([
        device.transferOut(outEp, bytes as unknown as BufferSource).then(
          (r): Attempt => (r.status === 'ok' ? 'ok' : 'halted'),
          (): Attempt => 'lost',
        ),
        expiry,
      ]);
    } finally {
      if (handle !== undefined) timers.clearTimeout(handle);
    }
  };

  const port: Ch340Port = {
    version,
    async write(bytes) {
      const first = await attempt(bytes);
      if (first === 'ok') return ok(undefined);
      if (first === 'lost') return err('lost');

      // Clear the endpoint whichever way it failed. A halt is sticky: leave it and every later
      // command fails behind this one, which is how a single bad write takes a whole outing.
      const cleared = await clearHalt('out', outEp);

      // Retry ONLY after a definite non-delivery. `halted` means the transfer completed and the
      // device refused it, so re-sending cannot duplicate anything. A timeout is indeterminate —
      // WebUSB has no way to cancel a transfer, so the original may still be in flight and may yet
      // reach the board. Re-sending then delivers the command twice, and while a repeated `cfg` is
      // harmless, a repeated `tof` is a second ranging exchange nobody asked for.
      if (first === 'halted' && cleared) {
        const second = await attempt(bytes);
        if (second === 'ok') return ok(undefined);
        if (second === 'lost') return err('lost');
      }
      return err('stalled');
    },
    async *read() {
      let halts = 0;
      while (!closed) {
        let r: USBInTransferResult;
        try {
          r = await device.transferIn(inEp, 64);
        } catch {
          return; // device lost — the caller detects it via the disconnect event (§ REQ-USB-6a)
        }
        if (r.status !== 'ok') {
          // A halted IN endpoint returns immediately and keeps doing so. Unhandled, the board goes
          // silent with no error raised anywhere and this loop spins. Clear it and carry on, but
          // not forever — see MAX_CONSECUTIVE_HALTS.
          if (++halts > MAX_CONSECUTIVE_HALTS || !(await clearHalt('in', inEp))) return;
          continue;
        }
        halts = 0;
        if (r.data != null && r.data.byteLength > 0) {
          yield new Uint8Array(r.data.buffer, r.data.byteOffset, r.data.byteLength);
        }
      }
    },
    async close() {
      closed = true;
      try {
        await device.close();
      } catch {
        /* already gone — nothing useful to do */
      }
    },
  };
  return ok(port);
}
